import type { AdminCrewReportRepatriationTableProps } from "../../models/admin-crew-report.models";
import {
  ADMIN_CREW_REPORT_REPATRIATION_COLUMNS,
  ADMIN_CREW_REPORT_STATISTICS_TITLE_CLASS_NAME,
  ADMIN_CREW_REPORT_STATISTICS_VIEW_LABELS,
} from "../../utils/admin-crew-report.constants";
import AdminCrewReportRepatriationTableRow from "./AdminCrewReportRepatriationTableRow";

const AdminCrewReportRepatriationTable = ({ statistics, view }: AdminCrewReportRepatriationTableProps) => {
  const viewLabels = ADMIN_CREW_REPORT_STATISTICS_VIEW_LABELS[view];
  const signOnDateSubject = view === "women" ? "women's" : "crew";
  const exclusionMessage = `${statistics.excludedVesselCount} ${
    statistics.excludedVesselCount === 1 ? "vessel was" : "vessels were"
  } excluded because one or more ${signOnDateSubject} sign-on dates are missing.`;

  return (
    <article
      data-test="admin-crew-report-repatriation-card"
      data-testid="admin-crew-report-repatriation-card"
      className="flex h-[250px] min-w-0 flex-col border border-[#D8E6E3] bg-white px-3 pb-0 pt-2 dark:border-gray-700 dark:bg-gray-800 md:col-span-2 xl:col-span-1 xl:ml-[min(0px,calc(24rem_-_100%))] xl:w-[max(100%,calc(200%_-_24rem))]"
    >
      <h2 className={ADMIN_CREW_REPORT_STATISTICS_TITLE_CLASS_NAME}>{viewLabels.repatriationTitle}</h2>

      {statistics.categories.length === 0 ? (
        <div className="flex min-h-0 flex-1 items-center justify-center px-3 text-center text-xs text-[#65706F] dark:text-gray-300">
          No vessels with complete sign-on dates are available.
        </div>
      ) : (
        <div
          data-test="admin-crew-report-repatriation-table-scroll"
          data-testid="admin-crew-report-repatriation-table-scroll"
          className="mt-1 min-h-0 flex-1 overflow-hidden"
        >
          <table className="flex h-full w-full border-collapse flex-col text-[10px] leading-3 text-[#24302E] dark:text-gray-100 sm:text-[11px]">
            <caption className="sr-only">
              Vessel counts by desk and the longest-serving {view === "women" ? "woman's" : "crew member's"} months on
              board.
            </caption>
            <thead className="flex-none">
              <tr className="grid grid-cols-[18%_repeat(5,minmax(0,1fr))] border-b border-[#8AA6A1] pr-1.5 dark:border-gray-500">
                <th scope="col" className="break-words px-1 py-1.5 text-left font-semibold">
                  Desk
                </th>
                {ADMIN_CREW_REPORT_REPATRIATION_COLUMNS.map((column) => (
                  <th key={column.key} scope="col" className="break-words px-1 py-1.5 text-center font-semibold">
                    {column.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody
              data-test="admin-crew-report-repatriation-table-body"
              data-testid="admin-crew-report-repatriation-table-body"
              aria-label={`Scrollable ${viewLabels.repatriationTitle.toLowerCase()} table rows`}
              tabIndex={0}
              className="block min-h-0 flex-1 overflow-y-auto overflow-x-hidden [scrollbar-gutter:stable] [scrollbar-width:thin] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-[#367C2B] [&::-webkit-scrollbar]:w-1.5"
            >
              {statistics.categories.map((category) => (
                <AdminCrewReportRepatriationTableRow key={category.name} category={category} />
              ))}
            </tbody>
            <tfoot className="flex-none">
              <tr className="grid grid-cols-[18%_repeat(5,minmax(0,1fr))] border-t border-[#8AA6A1] pr-1.5 font-semibold dark:border-gray-500">
                <th scope="row" className="break-words px-1 pb-[6px] pt-[5px] text-left">
                  Total Vessels
                </th>
                {ADMIN_CREW_REPORT_REPATRIATION_COLUMNS.map((column) => (
                  <td key={column.key} className="flex items-center justify-center px-1 pb-[6px] pt-[5px] text-center">
                    {statistics.totals[column.key]}
                  </td>
                ))}
              </tr>
            </tfoot>
          </table>
        </div>
      )}

      {statistics.excludedVesselCount > 0 && (
        <p
          data-test="admin-crew-report-repatriation-excluded"
          className="mt-1 flex-none text-[10px] leading-3 text-[#65706F] dark:text-gray-300"
        >
          {exclusionMessage}
        </p>
      )}
    </article>
  );
};

export default AdminCrewReportRepatriationTable;
